import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@components';
import { Translation, VariantType } from '@enums';
import { cn } from '@utils';

interface MapControllerProps {
  zoomLevel: number;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleReset: () => void;
}

const maximumZoom = 4;
const minimumZoom = 1;

const MapController: React.FC<MapControllerProps> = memo(({ zoomLevel, handleZoomIn, handleZoomOut, handleReset }) => {
  const { t: tCommon } = useTranslation(Translation.Common, { keyPrefix: 'button' });

  return (
    <div className="flex flex-row gap-3">
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button
          className={cn(
            'p-1.5 sm:p-2.5 w-fit h-fit rounded-md shadow-md enabled:shadow-none',
            zoomLevel >= maximumZoom && 'ring-slate-200 text-slate-400 bg-gray-50',
          )}
          variant={VariantType.Outlined}
          onClick={handleZoomIn}
          disabled={zoomLevel >= maximumZoom}
          adornment={<PlusIcon className="size-3 sm:size-4" />}
        />
      </motion.div>
      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
        <Button
          className={cn(
            'p-1.5 sm:p-2.5 w-fit h-fit rounded-md shadow-md enabled:shadow-none',
            zoomLevel <= minimumZoom && 'ring-slate-200 text-slate-400 bg-gray-50',
          )}
          variant={VariantType.Outlined}
          onClick={handleZoomOut}
          disabled={zoomLevel <= minimumZoom}
          adornment={<MinusIcon className="size-3 sm:size-4" />}
        />
      </motion.div>
      {zoomLevel !== 1 && (
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            className={cn(
              'y-1.5 px-2 sm:py-2 sm:px-3 ring-1 rounded-md shadow-md h-full',
              zoomLevel === 1 && 'ring-slate-200 text-slate-400 bg-gray-50',
            )}
            variant={VariantType.Outlined}
            disabled={zoomLevel === 1}
            onClick={handleReset}
          >
            <Text $level={6}>{tCommon('reset')}</Text>
          </Button>
        </motion.div>
      )}
    </div>
  );
});

export default MapController;
